import { useState, useEffect, useMemo, useRef } from "react";
import { useT } from "./i18n";
import { CURRICULUM, getCurriculum } from "./curriculum";
import { getGrammarPoints } from "./grammarLab";
import { trPoint } from "./GrammarLabView";
import { isolateInterpolation, getTranslatedTopicName } from "./utils";
import { buildWordEntries } from "./searchEntries.mjs";
import { normalizeFarsi } from "./farsiNormalize.mjs";
import SEARCH_INDEX from "./searchIndex.json";

const RESULT_CAP = 8;
const DEBOUNCE_MS = 200;

// Canonical (word_id -> {word, topic}) index, built once at module scope from the static
// Turkish curriculum. See searchEntries.mjs for the algorithm (shared with scripts/
// build-search-index.js so the two never diverge on which topic a word_id resolves to).
// Deliberately NOT run through getCurriculum(langCode) — this is a module-scope constant, built
// once at load, not per-render, so it can't simply take a langCode parameter the way the
// component-level usage below does. Making this per-language is real structural work (a
// per-language cache/map here, plus the same fix in scripts/build-search-index.js, which reads
// curriculum.js as raw text and shares no code path with this module at all) — tracked as its
// own follow-up (backlog task 61 covers the build-script half), not folded into this pass.
const WORD_ENTRIES = buildWordEntries(CURRICULUM);

function isTopicLocked(topic, premium, frozenTopicIds) {
  return !premium && !frozenTopicIds.has(topic.topic_id);
}

// ═══════════════════════════════════════════════════════════════
// GLOBAL SEARCH — header search trigger + overlay. Searches words
// (turkish/english), topics (topic_name), and Grammar Lab points
// (localized title/rule_summary) client-side over already-loaded data.
// ═══════════════════════════════════════════════════════════════
export function GlobalSearch({ premium, frozenTopicIds, onUpgrade, onSelectTopic, onSelectWord, onSelectGrammarPoint, baseLanguage = "English", targetLanguage = "Turkish" }) {
  const { t } = useT();
  const langCode = targetLanguage.toLowerCase();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const closeBtnRef = useRef(null);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query]);

  useEffect(() => { setActiveIndex(0); }, [debouncedQuery]);

  const qEn = debouncedQuery.trim().toLowerCase();
  // Turkish dotted İ/i lowercases incorrectly under the default (non-Turkish) locale — see
  // MyVocabularyView's identical fix. Applied only to the Turkish field.
  const qTr = debouncedQuery.trim().toLocaleLowerCase("tr-TR");

  // Base-language matching (Spanish/German/French/Farsi), against the bundled search index
  // built from cached /translations/ docs by scripts/build-search-index.js. `langIndex` is
  // undefined for English/Turkish/any language not (yet) covered — those fields simply never
  // match below, leaving behavior for those learners unchanged. A topic/word not yet present
  // in the index (new curriculum batch, not yet prewarmed) is handled the same way: it's just
  // absent, so the localized branch quietly no-ops and Turkish/English matching still applies.
  const langIndex = SEARCH_INDEX.index[baseLanguage];
  const isFarsi = baseLanguage === "Farsi";
  // Farsi has glyph variants that render identically but compare unequal (see
  // farsiNormalize.mjs) — both the query and the indexed value get normalized before
  // comparing, mirroring qTr's Turkish-specific handling above.
  const qBase = isFarsi ? normalizeFarsi(qEn) : qEn;
  const matchesLocalized = (localized) => {
    if (!localized) return false;
    const normalized = isFarsi ? normalizeFarsi(localized) : localized;
    return normalized.toLowerCase().includes(qBase);
  };

  const topicResults = useMemo(() => {
    if (!qEn) return [];
    return getCurriculum(langCode).filter(topic =>
      topic.topic_name.toLowerCase().includes(qEn) || matchesLocalized(langIndex?.topics?.[topic.topic_id])
    ).slice(0, RESULT_CAP);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qEn, qBase, langIndex, langCode]);

  const wordResults = useMemo(() => {
    if (!qTr && !qEn) return [];
    return WORD_ENTRIES.filter(({ word }) =>
      word.turkish.toLocaleLowerCase("tr-TR").includes(qTr) || word.english.toLowerCase().includes(qEn) ||
      matchesLocalized(langIndex?.words?.[word.word_id])
    ).slice(0, RESULT_CAP);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qTr, qEn, qBase, langIndex]);

  const grammarResults = useMemo(() => {
    if (!qEn) return [];
    return getGrammarPoints(langCode).filter(gp => {
      const title = trPoint(t, gp, "title", "title").toLowerCase();
      const rule = trPoint(t, gp, "ruleSummary", "rule_summary").toLowerCase();
      return title.includes(qEn) || rule.includes(qEn);
    }).slice(0, RESULT_CAP);
  }, [qEn, t, langCode]);

  const flatResults = useMemo(() => [
    ...topicResults.map(topic => ({ type: "topic", key: `gs-topic-${topic.topic_id}`, topic })),
    ...wordResults.map(({ word, topic }) => ({ type: "word", key: `gs-word-${word.word_id}`, word, topic })),
    ...grammarResults.map(gp => ({ type: "grammar", key: `gs-grammar-${gp.grammar_point_id}`, gp })),
  ], [topicResults, wordResults, grammarResults]);

  const close = () => {
    setOpen(false);
    setQuery("");
    triggerRef.current?.focus();
  };

  const handleSelect = (result) => {
    if (result.type === "topic") onSelectTopic?.(result.topic);
    else if (result.type === "word") onSelectWord?.(result.word, result.topic);
    else onSelectGrammarPoint?.(result.gp.grammar_point_id);
    close();
  };

  // Escape (global while open), arrow-key result navigation, Enter to select, and a 2-stop
  // Tab trap between the input and the close button — same trap shape as BottomNav's More menu.
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") { e.preventDefault(); close(); return; }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex(i => (flatResults.length ? (i + 1) % flatResults.length : 0));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex(i => (flatResults.length ? (i - 1 + flatResults.length) % flatResults.length : 0));
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        const r = flatResults[activeIndex];
        if (r) handleSelect(r);
        return;
      }
      if (e.key === "Tab") {
        const els = [inputRef.current, closeBtnRef.current].filter(Boolean);
        if (els.length < 2) return;
        const first = els[0], last = els[els.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, flatResults, activeIndex]);

  // Click-outside-to-close — listener attached only once `open` is already true (a browser tick
  // after the opening click finished), so the same click that opens the trigger can never also
  // fire this handler and instantly re-close it.
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (!panelRef.current?.contains(e.target) && !triggerRef.current?.contains(e.target)) close();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const activeKey = flatResults[activeIndex]?.key;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="global-search-trigger"
        onClick={() => setOpen(true)}
        aria-label={t("search.trigger")}
      >
        🔍
      </button>

      {open && (
        <div className="global-search-overlay">
          <div className="global-search-panel" ref={panelRef} role="dialog" aria-modal="true" aria-label={t("search.title")}>
            <div className="lesson-header gs-header">
              <button type="button" className="back-btn" ref={closeBtnRef} onClick={close}>{t("search.close")}</button>
              <div className="lesson-title">{t("search.title")}</div>
            </div>

            <div className="al-search-wrap gs-input-wrap">
              <input
                ref={inputRef}
                autoFocus
                type="text"
                className="text-input gs-search-input"
                role="combobox"
                aria-expanded="true"
                aria-controls="global-search-listbox"
                aria-activedescendant={activeKey}
                placeholder={t("search.placeholder")}
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
              {query && (
                <button type="button" className="gs-clear-btn" onClick={() => setQuery("")} aria-label={t("search.clear")}>✕</button>
              )}
            </div>

            <div id="global-search-listbox" role="listbox" aria-label={t("search.resultsLabel")} className="gs-results">
              {!debouncedQuery.trim() ? (
                <div className="empty-state">{t("search.emptyPrompt")}</div>
              ) : flatResults.length === 0 ? (
                <div className="empty-state">{isolateInterpolation(t("search.noResults"), "search", debouncedQuery)}</div>
              ) : (
                <>
                  {topicResults.length > 0 && (
                    <>
                      <div className="gs-group-label">{t("search.topicsLabel")}</div>
                      {topicResults.map(topic => {
                        const key = `gs-topic-${topic.topic_id}`;
                        const locked = isTopicLocked(topic, premium, frozenTopicIds);
                        return (
                          <div
                            key={key}
                            id={key}
                            role="option"
                            aria-selected={activeKey === key}
                            className={`gs-result-row${activeKey === key ? " gs-result-row--active" : ""}`}
                            onMouseEnter={() => setActiveIndex(flatResults.findIndex(r => r.key === key))}
                            onClick={() => handleSelect({ type: "topic", key, topic })}
                          >
                            <span className="gs-result-badge gs-result-badge--topic">{t("search.badgeTopic")}</span>
                            <span className="al-name gs-result-name">
                              <bdi>{getTranslatedTopicName(topic, baseLanguage)}</bdi>
                              {locked && <span className="al-pro-tag">{t("search.proTag")}</span>}
                            </span>
                            {locked && <span className="al-lock">🔒</span>}
                          </div>
                        );
                      })}
                    </>
                  )}
                  {wordResults.length > 0 && (
                    <>
                      <div className="gs-group-label">{t("search.wordsLabel")}</div>
                      {wordResults.map(({ word, topic }) => {
                        const key = `gs-word-${word.word_id}`;
                        const locked = isTopicLocked(topic, premium, frozenTopicIds);
                        return (
                          <div
                            key={key}
                            id={key}
                            role="option"
                            aria-selected={activeKey === key}
                            className={`gs-result-row${activeKey === key ? " gs-result-row--active" : ""}`}
                            onMouseEnter={() => setActiveIndex(flatResults.findIndex(r => r.key === key))}
                            onClick={() => handleSelect({ type: "word", key, word, topic })}
                          >
                            <span className="gs-result-badge gs-result-badge--word">{t("search.badgeWord")}</span>
                            <span className="al-name gs-result-name">
                              <bdi>{word.turkish}</bdi>
                              <span className="gs-result-sub"> — {word.english}</span>
                              {locked && <span className="al-pro-tag">{t("search.proTag")}</span>}
                            </span>
                            {locked && <span className="al-lock">🔒</span>}
                          </div>
                        );
                      })}
                    </>
                  )}
                  {grammarResults.length > 0 && (
                    <>
                      <div className="gs-group-label">{t("search.grammarLabel")}</div>
                      {grammarResults.map(gp => {
                        const key = `gs-grammar-${gp.grammar_point_id}`;
                        return (
                          <div
                            key={key}
                            id={key}
                            role="option"
                            aria-selected={activeKey === key}
                            className={`gs-result-row${activeKey === key ? " gs-result-row--active" : ""}`}
                            onMouseEnter={() => setActiveIndex(flatResults.findIndex(r => r.key === key))}
                            onClick={() => handleSelect({ type: "grammar", key, gp })}
                          >
                            <span className="gs-result-badge gs-result-badge--grammar">{t("search.badgeGrammar")}</span>
                            <span className="al-name gs-result-name">
                              {trPoint(t, gp, "title", "title")}
                              <span className="gs-result-sub"> — {trPoint(t, gp, "ruleSummary", "rule_summary")}</span>
                            </span>
                          </div>
                        );
                      })}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
